import React, { useCallback, useEffect, useRef, useState } from 'react';
import { chatFileBlob, downloadChatFile } from '../lib/chatLink';
import './VoiceNote.css';

/**
 * VOICE NOTES - Gordon wants to record one and send it in chat, the way
 * WhatsApp and Slack do it.
 *
 * Three pieces live here:
 *
 *  - `useVoiceRecorder`, the microphone and the MediaRecorder behind the
 *    composer's mic button. What it produces is an ordinary File, which the
 *    composer sends down the SAME road a chosen file takes - so on a ticket
 *    a voice note goes on the ticket's own Files, and on General or in a
 *    group it stays in the conversation. No new endpoint, no special case on
 *    the server, which already stores any type up to 10 MB.
 *  - `VoiceBar`, what the composer shows in place of the box while recording.
 *  - `VoicePlayer`, how an audio attachment is drawn in a message.
 *
 * The styles are in VoiceNote.css, `.vn-...`, not in ChatPage.css.
 */

/** Five minutes, then it stops and sends by itself, as WhatsApp's does. */
export const MAX_VOICE_SECONDS = 5 * 60;

// In this order: Chrome and Firefox take the first, Safari only the third.
const TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
const EXT = { 'audio/webm': 'webm', 'audio/mp4': 'm4a', 'audio/ogg': 'ogg' };

export const DENIED = 'Allow the microphone in your browser to record a voice note';

/** Whether this browser can record at all; the mic button is hidden if not. */
export const canRecord = () => typeof window !== 'undefined'
  && typeof navigator !== 'undefined'
  && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  && Boolean(window.MediaRecorder);

const pickType = () => {
  const MR = window.MediaRecorder;
  if (!MR || typeof MR.isTypeSupported !== 'function') return '';
  return TYPES.find((t) => {
    try { return MR.isTypeSupported(t); } catch { return false; }
  }) || '';
};

export const clock = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const s = Math.floor(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const two = (n) => String(n).padStart(2, '0');

/** "Voice note 14-32.webm" - the time it was recorded, the right extension. */
export const voiceFileName = (type, when = new Date()) => {
  const base = (type || '').split(';')[0].trim();
  return `Voice note ${two(when.getHours())}-${two(when.getMinutes())}.${EXT[base] || 'webm'}`;
};

/**
 * The recorder. `onRecorded(file)` is called with the finished File when Send
 * is pressed or five minutes run out; Cancel throws the recording away.
 * `resetKey` is the conversation: moving to another one discards whatever is
 * being recorded, for the same reason a chosen file does not follow you -
 * a note meant for Marco must not land on a ticket.
 *
 * The microphone is let go whenever recording ends, however it ends: sent,
 * cancelled, the conversation changed, or the page gone. A track left open
 * keeps the browser's red "recording" light on, which is the one thing
 * people notice most.
 */
export function useVoiceRecorder({ onRecorded, onError, resetKey }) {
  const [phase, setPhase] = useState('idle'); // idle | asking | recording
  const [elapsed, setElapsed] = useState(0);
  const stream = useRef(null);
  const recorder = useRef(null);
  const timer = useRef(null);
  const discard = useRef(false);
  const attempt = useRef(0);
  const began = useRef(0);
  const done = useRef(onRecorded);
  const fail = useRef(onError);
  done.current = onRecorded;
  fail.current = onError;

  const release = useCallback(() => {
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    if (stream.current) {
      stream.current.getTracks().forEach((t) => { try { t.stop(); } catch { /* gone */ } });
      stream.current = null;
    }
  }, []);

  /** Throw away anything in progress, including a permission still pending. */
  const cancel = useCallback(() => {
    attempt.current += 1;
    discard.current = true;
    const r = recorder.current;
    recorder.current = null;
    if (r && r.state !== 'inactive') {
      try { r.stop(); } catch { /* already stopped */ }
    }
    release();
    setPhase('idle');
    setElapsed(0);
  }, [release]);

  const finish = useCallback(() => {
    const r = recorder.current;
    if (!r || r.state === 'inactive') return;
    discard.current = false;
    if (timer.current) { clearInterval(timer.current); timer.current = null; }
    try { r.stop(); } catch { cancel(); }
  }, [cancel]);

  const start = useCallback(async () => {
    if (phase !== 'idle') return;
    const mine = ++attempt.current;
    setPhase('asking');
    if (fail.current) fail.current(null);
    let media;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      if (mine !== attempt.current) return;
      setPhase('idle');
      const name = err && err.name;
      if (fail.current) {
        fail.current(name === 'NotFoundError' || name === 'OverconstrainedError'
          ? 'No microphone was found. Plug one in to record a voice note.'
          : name === 'NotReadableError'
            ? 'The microphone is in use by another program.'
            : DENIED);
      }
      return;
    }
    // Cancelled, or moved elsewhere, while the browser was asking.
    if (mine !== attempt.current) {
      media.getTracks().forEach((t) => t.stop());
      return;
    }
    stream.current = media;
    const type = pickType();
    let r;
    try {
      r = type ? new window.MediaRecorder(media, { mimeType: type })
        : new window.MediaRecorder(media);
    } catch {
      release();
      setPhase('idle');
      if (fail.current) fail.current('This browser could not start recording.');
      return;
    }
    const chunks = [];
    discard.current = false;
    r.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };
    r.onstop = () => {
      release();
      if (recorder.current === r) recorder.current = null;
      setPhase('idle');
      setElapsed(0);
      if (discard.current || mine !== attempt.current) return;
      const kind = (r.mimeType || type || 'audio/webm').split(';')[0].trim();
      const blob = new Blob(chunks, { type: kind });
      const file = new File([blob], voiceFileName(kind), { type: kind });
      // How long it ran, for whoever needs to say so before the file is
      // decoded (a to-do note's attachment carries duration_ms).
      if (done.current) done.current(file, Math.max(0, Date.now() - began.current));
    };
    recorder.current = r;
    try {
      r.start(1000);
    } catch {
      recorder.current = null;
      release();
      setPhase('idle');
      if (fail.current) fail.current('This browser could not start recording.');
      return;
    }
    began.current = Date.now();
    setElapsed(0);
    setPhase('recording');
    timer.current = setInterval(() => {
      const s = (Date.now() - began.current) / 1000;
      setElapsed(Math.min(Math.floor(s), MAX_VOICE_SECONDS));
      if (s >= MAX_VOICE_SECONDS) finish();
    }, 250);
  }, [phase, release, finish]);

  // Another conversation, or the page gone: discard and let the mic go.
  useEffect(() => () => cancel(), [resetKey, cancel]);

  return { phase, elapsed, start, cancel, finish, recording: phase === 'recording' };
}

/** A microphone, drawn so it takes the theme's ink. */
export const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
    <rect x="9" y="3" width="6" height="11" rx="3" fill="none" stroke="currentColor"
      strokeWidth="2" />
    <path d="M5.5 11a6.5 6.5 0 0013 0M12 17.5V21M8.5 21h7" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** The mic button beside the paperclip. */
export function MicButton({ recorder, disabled }) {
  return (
    <button
      type="button"
      className="vn-mic"
      aria-label="Record a voice note"
      title="Record a voice note"
      disabled={disabled || recorder.phase !== 'idle'}
      onClick={recorder.start}
    >
      <MicIcon />
    </button>
  );
}

/**
 * In place of the box while recording: a red dot, how long so far, Cancel
 * and Send. Send takes the focus, because the mic button that had it has
 * gone and Space or Enter should then do the obvious thing.
 */
export function VoiceBar({ recorder }) {
  const left = MAX_VOICE_SECONDS - recorder.elapsed;
  return (
    <div className="vn-bar" role="group" aria-label="Recording a voice note">
      <span className="vn-dot" aria-hidden="true" />
      <span className="vn-time" role="timer" aria-label={`Recorded ${clock(recorder.elapsed)}`}>
        {clock(recorder.elapsed)}
      </span>
      <span className="vn-state">
        {left <= 30 ? `Stops in ${left}s` : 'Recording…'}
      </span>
      <button type="button" className="vn-cancel" onClick={recorder.cancel}>
        Cancel
      </button>
      <button
        type="button"
        className="vn-send"
        onClick={recorder.finish}
        aria-label="Stop and send voice note"
        autoFocus
      >
        Send
      </button>
    </div>
  );
}

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <path d="M7 4.5v15l12-7.5z" fill="currentColor" />
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
    <path d="M6.5 4.5h4v15h-4zM13.5 4.5h4v15h-4z" fill="currentColor" />
  </svg>
);
const DownIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">
    <path d="M12 4v11m-5-5l5 5 5-5M5 20h14" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * An audio attachment in a message. The bytes are FETCHED with the sign-in,
 * like every chat file (an <audio src> carries no token and would be
 * answered 401), and only on the first press of play - a conversation of
 * voice notes should not download them all just by being opened. The object
 * URL is let go when the message goes.
 *
 * The length is not known until the file is in hand: a recording from
 * Chrome's MediaRecorder reports an infinite duration to <audio>, so it is
 * read by decoding the file instead, and failing that, learned at the end.
 */
/*
 * `load` and `download` let something other than chat hand the bytes over -
 * a to-do note's voice notes come from /todos/attachments, not /chat/files -
 * and `durationMs`, when the server already knows the length, shows it
 * before anything is fetched.
 */
export function VoicePlayer({ file, onError, load = null, download = null, durationMs = null }) {
  const audio = useRef(null);
  const url = useRef(null);
  const gone = useRef(false);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [length, setLength] = useState(() => (Number(durationMs) > 0 ? Number(durationMs) / 1000 : null));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
      if (audio.current) {
        audio.current.pause();
        audio.current.removeAttribute('src');
        audio.current = null;
      }
      if (url.current) { URL.revokeObjectURL(url.current); url.current = null; }
    };
  }, [file.id]);

  const learnLength = async (blob) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx || typeof blob.arrayBuffer !== 'function') return;
      const ctx = new Ctx();
      try {
        const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());
        if (!gone.current && Number.isFinite(decoded.duration)) setLength(decoded.duration);
      } finally {
        if (typeof ctx.close === 'function') ctx.close().catch(() => {});
      }
    } catch { /* the length shows once it has played through */ }
  };

  const toggle = async () => {
    if (loading) return;
    const a = audio.current;
    if (a) {
      if (a.paused) a.play().catch(() => {});
      else a.pause();
      return;
    }
    setLoading(true);
    try {
      const blob = await (load ? load(file) : chatFileBlob(file.id));
      if (gone.current) return;
      url.current = URL.createObjectURL(blob);
      const el = new Audio();
      el.preload = 'auto';
      const known = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) setLength(el.duration);
      };
      el.addEventListener('loadedmetadata', known);
      el.addEventListener('durationchange', known);
      el.addEventListener('timeupdate', () => setAt(el.currentTime));
      el.addEventListener('play', () => setPlaying(true));
      el.addEventListener('pause', () => setPlaying(false));
      el.addEventListener('ended', () => {
        setPlaying(false);
        setLength((l) => l || el.currentTime || null);
        setAt(0);
      });
      el.src = url.current;
      audio.current = el;
      learnLength(blob);
      await el.play().catch((err) => {
        // A file this browser cannot play (Safari and a WebM, say) is still
        // there to download - say so rather than doing nothing.
        if (onError && err && err.name !== 'AbortError') {
          onError(`This browser cannot play ${file.file_name}. Download it instead.`);
        }
      });
    } catch (err) {
      if (onError) onError(err.response?.data?.error || `Could not play ${file.file_name}`);
    } finally {
      if (!gone.current) setLoading(false);
    }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await (download ? download(file) : downloadChatFile(file));
    } catch (err) {
      if (onError) onError(err.response?.data?.error || `Could not download ${file.file_name}`);
    } finally {
      if (!gone.current) setSaving(false);
    }
  };

  const pct = length ? Math.min(100, (at / length) * 100) : 0;
  const lengthText = length ? clock(length) : null;
  const label = playing
    ? `Pause voice note${lengthText ? `, ${clock(at)} of ${lengthText}` : ''}`
    : `Play voice note${lengthText ? `, ${lengthText}` : ''}`;

  return (
    <span className="vn-player" data-playing={playing || undefined}>
      <button
        type="button"
        className="vn-play"
        onClick={toggle}
        aria-label={label}
        title={label}
        aria-busy={loading || undefined}
      >
        {playing ? <PauseIcon /> : <PlayIcon />}
      </button>
      <span className="vn-body">
        <span className="vn-track" aria-hidden="true">
          <span className="vn-fill" style={{ width: `${pct}%` }} />
        </span>
        <span className="vn-meta">
          <span className="vn-name">Voice note</span>
          <span className="vn-len">
            {loading ? 'Loading…'
              : playing || at > 0 ? `${clock(at)}${lengthText ? ` / ${lengthText}` : ''}`
                : (lengthText || '')}
          </span>
        </span>
      </span>
      <button
        type="button"
        className="vn-download"
        onClick={save}
        disabled={saving}
        aria-label={`Download ${file.file_name}`}
        title={`Download ${file.file_name}`}
      >
        <DownIcon />
      </button>
    </span>
  );
}
